import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IndianRupee, TrendingUp, AlertTriangle, Users, ChevronDown, ChevronUp,
  RefreshCw, LayoutDashboard,
} from 'lucide-react';
import { dashboardAPI, aiAPI } from '@/lib/api';
import { toast } from 'sonner';
import AIInsightsStrip from '@/components/dashboard/AIInsightsStrip';
import SalesOverviewChart from '@/components/dashboard/SalesOverviewChart';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const KPICard = ({ icon: Icon, label, value, sub, color = 'text-emerald-600', bg = 'bg-emerald-50' }) => (
  <div className="bg-card rounded-xl border border-border p-5 flex flex-col gap-2">
    <div className="flex items-center gap-2 text-muted-foreground text-sm">
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </div>
    <div className={`text-3xl font-bold ${color}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export default function DashboardInsights() {
  const { data: summary, isLoading: loading, error: summaryError } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardAPI.getSummary().then(r => r.data),
    // uses the app-wide 5 min default staleTime — re-navigating to the
    // dashboard within that window is instant and skips the network call.
  });
  useEffect(() => {
    if (summaryError) toast.error('Failed to load dashboard summary');
  }, [summaryError]);

  const [insights, setInsights] = useState([]);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const loadInsights = async () => {
    if (insightsLoading) return;
    setInsightsLoading(true);
    try {
      const res = await aiAPI.getInsights();
      setInsights(res.data?.insights || []);
    } catch {
      toast.error('Could not load AI insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  const toggleInsights = () => {
    const next = !insightsOpen;
    setInsightsOpen(next);
    if (next && insights.length === 0) loadInsights();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <LayoutDashboard className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
        </div>
        <p className="text-sm text-muted-foreground">Welcome back! Here's what's happening today.</p>
      </div>

      {/* KPI Tiles */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card rounded-xl border border-border p-5 h-28 animate-pulse bg-muted/30" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            icon={IndianRupee}
            label="Today's Revenue"
            value={fmt(summary?.today_revenue)}
            sub={`${fmtNum(summary?.today_transactions)} Transactions`}
            color="text-emerald-600"
          />
          <KPICard
            icon={TrendingUp}
            label="Pending Khata"
            value={fmt(summary?.pending_khata)}
            sub={summary?.khata_customers ? `${fmtNum(summary.khata_customers)} customers owe` : 'No outstanding credit'}
            color="text-orange-500"
          />
          <KPICard
            icon={AlertTriangle}
            label="Low Stock Alerts"
            value={fmtNum(summary?.low_stock_count)}
            sub="items need restock"
            color={summary?.low_stock_count > 0 ? 'text-red-500' : 'text-emerald-600'}
          />
          <KPICard
            icon={Users}
            label="Inventory"
            value={fmtNum(summary?.total_products)}
            sub={`${fmtNum(summary?.total_customers)} Customers`}
            color="text-blue-600"
          />
        </div>
      )}

      {/* Sales Overview */}
      <SalesOverviewChart />

      {/* AI Business Insights */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div
          role="button"
          tabIndex={0}
          onClick={toggleInsights}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleInsights(); }}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">✨</span>
            <span className="font-semibold text-foreground">AI Business Insights</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={e => { e.stopPropagation(); loadInsights(); }}
              disabled={insightsLoading}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted/60 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${insightsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {insightsOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>

        {insightsOpen && (
          <div className="px-5 pb-5">
            {insightsLoading ? (
              <div className="text-sm text-muted-foreground">Generating insights…</div>
            ) : insights.length === 0 ? (
              <div className="text-sm text-muted-foreground">No insights available. Make sure your store has sales data.</div>
            ) : (
              <AIInsightsStrip insights={insights} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
